import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './WarehouseComparison.css';
import { getAuthToken } from '../utils/auth';

function WarehouseComparison({ warehouses, startDate, endDate, country, orderType, excludeCarriers, apiUrl, onBack, onFilterChange }) {
  const API_URL = apiUrl || process.env.REACT_APP_API_URL || 'http://localhost:3001/api/dashboard';
  const [warehouseData, setWarehouseData] = useState({});
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ warehouse: null, key: 'picks', direction: 'desc' });
  const [availableDates, setAvailableDates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);

  const getAuthHeaders = () => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchAllWarehouseData();
  }, [warehouses, startDate, endDate, country, orderType, excludeCarriers]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const headers = getAuthHeaders();
      const [datesRes, countriesRes, orderTypesRes] = await Promise.all([
        axios.get(`${API_URL}/available-dates`, { headers }),
        axios.get(`${API_URL}/countries`, { headers }),
        axios.get(`${API_URL}/order-types`, { headers })
      ]);
      setAvailableDates(datesRes.data.dates || []);
      setCountries(countriesRes.data.countries || []);
      setOrderTypes(orderTypesRes.data.orderTypes || []);
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchAllWarehouseData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (country && country !== 'All') params.append('country', country);
      if (orderType && orderType !== 'All') params.append('orderType', orderType);
      if (excludeCarriers) params.append('excludeCarriers', 'true');

      const headers = getAuthHeaders();
      const promises = warehouses.map(warehouse =>
        axios.get(`${API_URL}/picker-details/${warehouse}?${params}`, { headers })
      );

      const responses = await Promise.all(promises);
      const data = {};
      responses.forEach((response, index) => {
        data[warehouses[index]] = response.data;
      });

      setWarehouseData(data);
    } catch (err) {
      console.error('Error fetching warehouse data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (warehouse, key) => {
    let direction = 'desc';
    if (sortConfig.warehouse === warehouse && sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ warehouse, key, direction });
  };

  const getSortedPickers = (warehouse, pickers) => {
    if (!pickers || sortConfig.warehouse !== warehouse) return pickers;

    return [...pickers].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (sortConfig.direction === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
  };

  const formatNumber = (num) => {
    return num.toLocaleString('en-US');
  };

  const formatCurrency = (num) => {
    return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatWeight = (num) => {
    return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' lbs';
  };

  const getSortIndicator = (warehouse, key) => {
    if (sortConfig.warehouse !== warehouse || sortConfig.key !== key) return ' ⇅';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const orderTypeLabels = {
    'A': 'Assemblies',
    'C': 'Customer',
    'T': 'Transfers'
  };

  // Calculate grand totals across all warehouses
  const calculateGrandTotals = () => {
    let totals = {
      picks: 0,
      lines: 0,
      quantity: 0,
      weight: 0,
      dollars: 0
    };

    Object.values(warehouseData).forEach(data => {
      totals.picks += data.totalPicks || 0;
      if (data.pickers) {
        data.pickers.forEach(picker => {
          totals.lines += picker.lines || 0;
          totals.quantity += picker.quantity || 0;
          totals.weight += picker.weight || 0;
          totals.dollars += picker.dollars || 0;
        });
      }
    });

    return totals;
  };

  // Calculate warehouse totals
  const calculateWarehouseTotals = (data) => {
    let totals = {
      picks: data.totalPicks || 0,
      lines: 0,
      quantity: 0,
      weight: 0,
      dollars: 0
    };

    if (data.pickers) {
      data.pickers.forEach(picker => {
        totals.lines += picker.lines || 0;
        totals.quantity += picker.quantity || 0;
        totals.weight += picker.weight || 0;
        totals.dollars += picker.dollars || 0;
      });
    }

    return totals;
  };

  if (loading) {
    return <div className="comparison-loading">Loading comparison data...</div>;
  }

  const grandTotals = calculateGrandTotals();

  return (
    <div className="warehouse-comparison">
      <div className="comparison-header">
        <button className="back-btn" onClick={onBack}>
          ← Back to Summary
        </button>
        <h3>Warehouse Comparison</h3>
        <p className="comparison-subtitle">Comparing {warehouses.length} warehouses</p>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <label>Start Date:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              const value = e.target.value;
              onFilterChange({ startDate: value, endDate: value === '' ? '' : endDate });
            }}
          />
        </div>

        <div className="filter-group">
          <label>End Date:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => onFilterChange({ endDate: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <label>Country:</label>
          <select value={country} onChange={(e) => onFilterChange({ country: e.target.value })}>
            <option value="">All</option>
            {countries.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Order Type:</label>
          <select value={orderType} onChange={(e) => onFilterChange({ orderType: e.target.value })}>
            <option value="">All</option>
            {orderTypes.map(type => (
              <option key={type} value={type}>{orderTypeLabels[type] || type}</option>
            ))}
          </select>
        </div>

        <div className="filter-group checkbox-group">
          <label>
            <input
              type="checkbox"
              checked={excludeCarriers}
              onChange={(e) => onFilterChange({ excludeCarriers: e.target.checked })}
            />
            Exclude Carriers: Z/WC/HFSK
          </label>
        </div>
      </div>

      {/* Grand Totals Section */}
      <div className="grand-totals-section">
        <h4>Grand Totals Across All Selected Warehouses</h4>
        <div className="totals-grid">
          <div className="total-card">
            <span className="total-label">Total Picks</span>
            <span className="total-value">{formatNumber(grandTotals.picks)}</span>
          </div>
          <div className="total-card">
            <span className="total-label">Total Lines</span>
            <span className="total-value">{formatNumber(grandTotals.lines)}</span>
          </div>
          <div className="total-card">
            <span className="total-label">Total Quantity</span>
            <span className="total-value">{formatNumber(grandTotals.quantity)}</span>
          </div>
          <div className="total-card">
            <span className="total-label">Total Weight</span>
            <span className="total-value">{formatWeight(grandTotals.weight)}</span>
          </div>
          <div className="total-card">
            <span className="total-label">Total Dollars</span>
            <span className="total-value">{formatCurrency(grandTotals.dollars)}</span>
          </div>
        </div>
      </div>

      {/* Individual Warehouse Totals */}
      <div className="warehouse-totals-section">
        <h4>Individual Warehouse Totals</h4>
        <div className="warehouse-totals-grid">
          {warehouses.map(warehouse => {
            const data = warehouseData[warehouse];
            if (!data) return null;
            const totals = calculateWarehouseTotals(data);

            return (
              <div key={warehouse} className="warehouse-total-card">
                <h5>{warehouse}</h5>
                <div className="warehouse-total-metrics">
                  <div className="metric-row">
                    <span>Picks:</span>
                    <span>{formatNumber(totals.picks)}</span>
                  </div>
                  <div className="metric-row">
                    <span>Lines:</span>
                    <span>{formatNumber(totals.lines)}</span>
                  </div>
                  <div className="metric-row">
                    <span>Quantity:</span>
                    <span>{formatNumber(totals.quantity)}</span>
                  </div>
                  <div className="metric-row">
                    <span>Weight:</span>
                    <span>{formatWeight(totals.weight)}</span>
                  </div>
                  <div className="metric-row">
                    <span>Dollars:</span>
                    <span>{formatCurrency(totals.dollars)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Picker Details by Warehouse */}
      <div className="picker-details-section">
        <h4>Picker Details by Warehouse</h4>
        {warehouses.map(warehouse => {
          const data = warehouseData[warehouse];
          if (!data || !data.pickers) return null;

          const sortedPickers = getSortedPickers(warehouse, data.pickers);

          return (
            <div key={warehouse} className="warehouse-picker-section">
              <h5>{warehouse} - Pickers</h5>
              <div className="picker-table-container">
                <table className="picker-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort(warehouse, 'name')}>
                        Picker Name{getSortIndicator(warehouse, 'name')}
                      </th>
                      <th onClick={() => handleSort(warehouse, 'picks')}>
                        Picks{getSortIndicator(warehouse, 'picks')}
                      </th>
                      <th onClick={() => handleSort(warehouse, 'lines')}>
                        Lines{getSortIndicator(warehouse, 'lines')}
                      </th>
                      <th onClick={() => handleSort(warehouse, 'quantity')}>
                        Quantity{getSortIndicator(warehouse, 'quantity')}
                      </th>
                      <th onClick={() => handleSort(warehouse, 'weight')}>
                        Weight{getSortIndicator(warehouse, 'weight')}
                      </th>
                      <th onClick={() => handleSort(warehouse, 'dollars')}>
                        Dollars{getSortIndicator(warehouse, 'dollars')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPickers.map((picker, index) => (
                      <tr key={index}>
                        <td className="picker-name">{picker.name}</td>
                        <td>{formatNumber(picker.picks)}</td>
                        <td>{formatNumber(picker.lines)}</td>
                        <td>{formatNumber(picker.quantity)}</td>
                        <td>{formatWeight(picker.weight)}</td>
                        <td>{formatCurrency(picker.dollars)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default WarehouseComparison;
